import { createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
    /** What a component reads: `theme.palette.chat.canvas`. */
    interface Palette {
        /**
         * Surfaces specific to the AI chat.
         *
         * The chat needs a handful of near-neutral backgrounds that are neither
         * "paper" nor "default" in the ordinary sense -- a message bubble and a
         * code block are not the same surface. They used to be picked inline
         * with `palette.mode === 'dark' ? grey.x : grey.y`, repeated in four
         * places across three files, which is how a dark-mode chat ended up with
         * a white panel inside it.
         *
         * Naming them by role rather than by colour means the scheme decides the
         * value, and a component can only get this right by asking for what it
         * wants.
         */
        chat?: ChatPalette;
    }

    /** What `createTheme` accepts. Both halves are needed: the input is
     *  `PaletteOptions`, the value components receive is `Palette`. */
    interface PaletteOptions {
        chat?: ChatPalette;
    }

    interface ChatPalette {
        /** The scrolling thread behind the bubbles. */
        canvas: string;
        /** An assistant message bubble. */
        bubble: string;
        /** A fenced code block inside a reply. */
        codeBlock: string;
        /** Inline `code` spans inside a reply. */
        inlineCode: string;
    }
}

/**
 * The smallest comfortable touch target, in px.
 *
 * WCAG 2.5.8 (AA) sets the floor at 24x24; 44x44 is the size that is actually
 * reliable with a thumb, and is what the platform guidelines ask for. Anything
 * between the two passes the audit and still gets mis-tapped.
 */
const TOUCH_TARGET_PX = 44;

/**
 * Button sizing, keyed on the input device rather than the viewport.
 *
 * A phone is not the only way to have a coarse pointer -- a touchscreen laptop
 * or a tablet at desk width counts too -- so the rule is `pointer: coarse`, not
 * a breakpoint. On a mouse nothing changes and the chat keeps its density.
 *
 * This is a `MuiIconButton`/`MuiButton` override rather than per-component
 * `sx`, because the buttons in this app were individually sized all over the
 * place and the ones that were too small were the ones nobody had looked at.
 */
const touchTargets = {
    '@media (pointer: coarse)': {
        minWidth: TOUCH_TARGET_PX,
        minHeight: TOUCH_TARGET_PX,
    },
};

/**
 * The values below are the palette entries they replace, spelled out:
 * grey.50 #fafafa, grey.100 #f5f5f5, grey.200 #eeeeee, grey.800 #424242 and
 * grey.900 #212121. They are repeated rather than referenced because
 * `colorSchemes` is resolved before the palette it extends exists. Tests assert
 * the resolved colours, so a change here is a visible change and not a silent
 * one.
 */
export const theme = createTheme({
    colorSchemes: {
        light: {
            palette: {
                chat: {
                    canvas: '#fafafa',
                    bubble: '#f5f5f5',
                    codeBlock: '#eeeeee',
                    inlineCode: '#f5f5f5',
                },
            },
        },
        dark: {
            palette: {
                chat: {
                    canvas: '#212121',
                    bubble: '#424242',
                    codeBlock: '#212121',
                    inlineCode: '#212121',
                },
            },
        },
    },
    components: {
        MuiIconButton: {
            styleOverrides: { root: touchTargets },
        },
        MuiButton: {
            styleOverrides: { root: touchTargets },
        },
        MuiListItemButton: {
            styleOverrides: { root: touchTargets },
        },
    },
});
